import React from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers';
import {CSS} from '@dnd-kit/utilities';
import ChunkCard, {ChunkVisualState} from './ChunkCard';
import AddChunkCard from './AddChunkCard';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import styles from './DraggableChunks.module.css';

interface DraggableChunksProps {
  chunks: GeneratedVideoChunk[];
  lockedChunkIds?: Set<string>;
  selectedChunk?: GeneratedVideoChunk;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  onSelectChunk: (chunk: GeneratedVideoChunk) => void;
  onAddFromLibrary: () => void;
  onAddFromStoryblocks: () => void;
  addDisabled?: boolean;
  newChunkIds?: Set<string>;
  onChunksReorder: (
    chunks: GeneratedVideoChunk[],
    activeChunkId: string,
    newIndex: number,
  ) => void;
  totalTimelineSeconds: number;
  pixelsPerSecond: number;
  hideChunkDelete?: boolean;
}

const SortableChunkCard: React.FC<{
  chunk: GeneratedVideoChunk;
  state: ChunkVisualState;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  onSelectChunk: (chunk: GeneratedVideoChunk) => void;
  pixelsPerSecond: number;
  showNewBadge?: boolean;
  locked?: boolean;
  hideDelete?: boolean;
}> = ({
  chunk,
  state,
  onEditChunk,
  onSelectChunk,
  pixelsPerSecond,
  showNewBadge,
  locked,
  hideDelete,
}) => {
  const {setNodeRef, attributes, listeners, transform, transition} =
    useSortable({id: chunk.id, disabled: locked});

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChunkCard
        chunk={chunk}
        state={state}
        onEditChunk={onEditChunk}
        onSelectChunk={onSelectChunk}
        pixelsPerSecond={pixelsPerSecond}
        dragHandleProps={locked ? undefined : {...attributes, ...listeners}}
        showNewBadge={showNewBadge}
        locked={locked}
        hideDelete={hideDelete}
      />
    </div>
  );
};

export const DraggableChunks: React.FC<DraggableChunksProps> = ({
  chunks,
  lockedChunkIds,
  selectedChunk,
  onEditChunk,
  onSelectChunk,
  onAddFromLibrary,
  onAddFromStoryblocks,
  addDisabled = false,
  newChunkIds,
  onChunksReorder,
  pixelsPerSecond,
  hideChunkDelete = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragEnd = (event: any) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    if (lockedChunkIds?.has(active.id) || lockedChunkIds?.has(over.id)) {
      return;
    }

    const oldIndex = chunks.findIndex(chunk => chunk.id === active.id);
    const newIndex = chunks.findIndex(chunk => chunk.id === over.id);

    const reordered = arrayMove(chunks, oldIndex, newIndex);
    onChunksReorder(reordered, active.id, newIndex);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={chunks.map(c => c.id)}
        strategy={horizontalListSortingStrategy}>
        <div
          className={styles.chunksListContainer}
          style={{
            display: 'flex',
          }}>
          {chunks.map(chunk => (
            <SortableChunkCard
              key={chunk.id}
              chunk={chunk}
              state={
                !chunk?.include
                  ? 'deleted'
                  : selectedChunk?.id === chunk.id
                  ? 'selected'
                  : selectedChunk
                  ? 'dimmed'
                  : 'normal'
              }
              onEditChunk={onEditChunk}
              onSelectChunk={onSelectChunk}
              pixelsPerSecond={pixelsPerSecond}
              showNewBadge={newChunkIds?.has(chunk.id)}
              locked={lockedChunkIds?.has(chunk.id)}
              hideDelete={hideChunkDelete}
            />
          ))}
          <AddChunkCard
            onAddFromLibrary={onAddFromLibrary}
            onAddFromStoryblocks={onAddFromStoryblocks}
            disabled={addDisabled}
          />
        </div>
      </SortableContext>
    </DndContext>
  );
};
